/**
 * 市场产品调查 · Market Product Survey
 *
 * Single-file MVP that handles two states in one route:
 *   - No survey selected  →  survey list + "新建调查" button
 *   - Survey selected     →  product grid + filters + photo upload dialog
 *
 * 2026-08 · Phase 1 covers CRUD + photo upload with preset info + basic
 * filters. Excel import/export and per-product edit dialog live in Phase 2.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Plus, Upload, RefreshCw, X, ChevronLeft, ChevronRight, Store as StoreIcon, Trash2, Loader2, Check, List as ListIcon, Grid3x3, DownloadCloud, ZoomIn, Star, CheckSquare, Link2, Copy, ShieldCheck, Filter } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import { FavGroupBar, FavGroupPicker } from "@/components/market/FavGroups";
import { MobileFilterSheet } from "@/components/MobileFilterSheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { photoThumb, photoFull } from "@/lib/photoUrl";
import { maybeCompressImage } from "@/lib/compressImage";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";

// Fixed photo-type options (2026-08-01 user spec). We used to rely on a
// dictionary the user could edit at will; the vocabulary has now settled to
// exactly these four values, so hard-coding them keeps the UI simple and the
// data consistent.
const PHOTO_TYPES = ["packaging", "New products", "Assortment", "Display"];

/**
 * 2026-10-10 · Thumb helper used by every product-grid card. Tries thumb_url
 * first (if present), then falls back to url, then shows the "no image"
 * placeholder when both 404. Fixes the gray-box issue when FM-synced photos
 * point at R2 keys that no longer exist in the bucket.
 */
function SurveyPhotoThumb({ photo, placeholder }) {
  const hasThumb = Boolean(photo?.thumb_url);
  const hasUrl = Boolean(photo?.url);
  // 0 = try thumb_url, 1 = try url, 2 = broken (show placeholder).
  // If there's no thumb_url, start at stage 1 so we don't waste a render on it.
  const [stage, setStage] = useState(hasThumb ? 0 : 1);
  useEffect(() => { setStage(hasThumb ? 0 : 1); }, [photo?.url, photo?.thumb_url, hasThumb]);
  const broken = stage === 2 || !photo || (!hasThumb && !hasUrl);
  if (broken) {
    return (
      <div className="w-full h-full flex items-center justify-center text-xs text-muted-foreground">
        {placeholder}
      </div>
    );
  }
  const src = stage === 0 ? photoFull(photo.thumb_url) : photoFull(photo.url);
  return (
    <img
      src={src}
      alt=""
      className="w-full h-full object-contain"
      loading="lazy"
      onError={() => setStage((s) => (s === 0 && hasUrl ? 1 : 2))}
    />
  );
}

export default function MarketSurveyPage() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const canEdit = ["admin", "export", "sales_manager", "product_manager", "production_manager", "buyer", "gt_export", "market_surveyor"].includes(user?.role);

  // View mode: "products" (default cross-survey product grid) | "surveys" (legacy survey list)
  const [view, setView] = useState("products");

  const [surveys, setSurveys] = useState([]);
  const [active, setActive] = useState(null);
  const [products, setProducts] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [totalProducts, setTotalProducts] = useState(0);
  // Selection persists in localStorage for 24h so refreshing the browser or
  // navigating away doesn't wipe a batch that the user built up over the day.
  // 2026-02 · Scope the storage key to `user.email` so multiple accounts on
  // the same shared browser don't leak selections into each other's session
  // (user reported seeing another user's ticks).
  const SELECTION_STORAGE_KEY = `marketSurvey.selected.v2::${(user?.email || "anon").toLowerCase()}`;
  const SELECTION_TTL_MS = 24 * 60 * 60 * 1000;
  const [selected, setSelected] = useState(() => {
    try {
      // Sweep any legacy shared-key entry from previous versions so it can't
      // still bleed into this session.
      localStorage.removeItem("marketSurvey.selected.v1");
      const raw = localStorage.getItem(SELECTION_STORAGE_KEY);
      if (!raw) return new Set();
      const { ids, expires } = JSON.parse(raw);
      if (!expires || Date.now() > expires) {
        localStorage.removeItem(SELECTION_STORAGE_KEY);
        return new Set();
      }
      return new Set(Array.isArray(ids) ? ids : []);
    } catch { return new Set(); }
  });
  // When the logged-in user changes (e.g. logout → login on shared PC), reset
  // the in-memory selection and re-hydrate from the new user's storage key.
  useEffect(() => {
    try {
      const raw = localStorage.getItem(SELECTION_STORAGE_KEY);
      if (!raw) { setSelected(new Set()); return; }
      const { ids, expires } = JSON.parse(raw);
      if (!expires || Date.now() > expires) {
        localStorage.removeItem(SELECTION_STORAGE_KEY);
        setSelected(new Set());
        return;
      }
      setSelected(new Set(Array.isArray(ids) ? ids : []));
    } catch { setSelected(new Set()); }
  }, [SELECTION_STORAGE_KEY]);
  useEffect(() => {
    try {
      if (selected.size === 0) {
        localStorage.removeItem(SELECTION_STORAGE_KEY);
      } else {
        localStorage.setItem(SELECTION_STORAGE_KEY, JSON.stringify({
          ids: Array.from(selected),
          expires: Date.now() + SELECTION_TTL_MS,
        }));
      }
    } catch { /* storage disabled — fine, in-memory still works */ }
  }, [selected, SELECTION_STORAGE_KEY]);

  const [filters, setFilters] = useState({ category: "", project: "", purpose: "", search: "" });
  // 2026-10-11 · Admin-only "只看已选" toggle. Also auto-reset when the user
  // clears their selection so the empty filter doesn't trap them on an empty
  // grid without a visible "selected" count beside it.
  const [showOnlySelected, setShowOnlySelected] = useState(false);
  useEffect(() => {
    if (selected.size === 0 && showOnlySelected) setShowOnlySelected(false);
  }, [selected.size, showOnlySelected]);
  // Top-level filters for the cross-survey products view
  const [globalFilters, setGlobalFilters] = useState({ store: "", date_from: "", date_to: "", category: "", project: "", purpose: "", search: "" });
  // 2026-06 · 我的收藏 — 只看自己收藏的调查产品
  const [favOnly, setFavOnly] = useState(false);
  const [favGroups, setFavGroups] = useState([]);
  const [favMeta, setFavMeta] = useState({ total: 0, ungrouped: 0 });
  const [activeFavGroup, setActiveFavGroup] = useState("");
  // Server-side pagination — the backend returns true `total` from the DB and
  // we fetch the next 20 on demand. Client-side slicing capped everything at
  // 2000 rows before, which was blatantly wrong once FM sync crossed 5k+.
  const PAGE_SIZE = 240;
  const [pageLoading, setPageLoading] = useState(false);

  const [stores, setStores] = useState([]);
  const [activeStores, setActiveStores] = useState([]);
  const [activeProjects, setActiveProjects] = useState([]);
  const [projects, setProjects] = useState([]);
  const [purposes, setPurposes] = useState([]);
  const [categories, setCategories] = useState([]);

  const [newSurveyOpen, setNewSurveyOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);

  const loadSurveys = async () => {
    try { const { data } = await api.get("/market/surveys"); setSurveys(data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const loadDicts = async () => {
    try {
      const [s, p, pu, settings, active, activeProj] = await Promise.all([
        api.get("/market/dict/stores"), api.get("/market/dict/projects"), api.get("/market/dict/purposes"),
        api.get("/settings"),
        api.get("/market/dict-active/stores"),
        api.get("/market/dict-active/projects"),
      ]);
      setStores(s.data); setProjects(p.data); setPurposes(pu.data);
      setCategories(settings.data?.categories || []);
      setActiveStores(active.data || []);
      setActiveProjects(activeProj.data || []);
    } catch { /* silent */ }
  };
  const loadProducts = useCallback(async () => {
    if (!active) return;
    try {
      const params = {};
      if (filters.category) params.category = filters.category;
      if (filters.project) params.project = filters.project;
      if (filters.purpose) params.purpose = filters.purpose;
      if (filters.search) params.search = filters.search;
      const { data } = await api.get(`/market/surveys/${active.id}/products`, { params });
      setProducts(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  }, [active, filters]);

  // 2026-02 · Guard against out-of-order responses. Every fetch increments
  // `reqSeqRef`; when the response comes back we compare against the latest
  // value — if a newer request has since fired, we drop this stale payload.
  // Fixes user report: fast typing in the search box mixed results from
  // multiple in-flight queries.
  const reqSeqRef = useRef(0);
  const loadAllProducts = useCallback(async (opts = {}) => {
    const append = !!opts.append;
    const seq = ++reqSeqRef.current;
    try {
      setPageLoading(true);
      // Read current length synchronously via functional setState so this
      // callback stays stable across appends and doesn't retrigger effects.
      let offset = 0;
      if (append) {
        offset = await new Promise((res) => {
          setAllProducts((prev) => { res(prev.length); return prev; });
        });
      }
      const params = { limit: PAGE_SIZE, offset };
      Object.entries(globalFilters).forEach(([k, v]) => { if (v) params[k] = v; });
      if (favOnly) {
        params.favorites = "1";
        if (activeFavGroup && activeFavGroup !== "__none__") params.fav_group = activeFavGroup;
      }
      const { data } = await api.get("/market/products", { params });
      // Discard stale responses — only the latest sequence number wins.
      if (seq !== reqSeqRef.current) return;
      const items = Array.isArray(data) ? data : (data?.items || []);
      const total = Array.isArray(data) ? items.length : (data?.total ?? items.length);
      // 2026-02 · Dedupe by id on append. Skip-based pagination on a list that
      // grows during a session (FM sync inserts new rows at the top) can push
      // page 1's tail into page 2, so the naïve concat would show duplicates.
      // Same guard also protects against React StrictMode double-invocation.
      setAllProducts((prev) => {
        if (!append) return items;
        const seen = new Set(prev.map((p) => p.id));
        return [...prev, ...items.filter((p) => !seen.has(p.id))];
      });
      setTotalProducts(total);
    } catch (e) {
      if (seq !== reqSeqRef.current) return;
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      if (seq === reqSeqRef.current) setPageLoading(false);
    }
  }, [globalFilters, favOnly, activeFavGroup]);

  const loadFavGroups = useCallback(async () => {
    try {
      const { data } = await api.get("/market/fav-groups");
      setFavGroups(data.groups || []);
      setFavMeta({ total: data.total || 0, ungrouped: data.ungrouped || 0 });
    } catch { /* 非致命 */ }
  }, []);

  useEffect(() => { if (favOnly) loadFavGroups(); }, [favOnly, loadFavGroups]);

  const applyGroups = (pid, groupIds) => {
    const patch = (list) => list.map((x) => (x.id === pid ? { ...x, is_favorite: true, fav_group_ids: groupIds } : x));
    setAllProducts(patch);
    setProducts(patch);
    loadFavGroups();
    if (favOnly && activeFavGroup) loadAllProducts();
  };

  // 2026-06 · 我的收藏 — 乐观切换，失败回滚
  const toggleFavorite = async (p) => {
    const next = !p.is_favorite;
    const flip = (list) => list.map((x) => (x.id === p.id ? { ...x, is_favorite: next } : x));
    setAllProducts(flip);
    setProducts(flip);
    try {
      await api.post(`/market/products/${p.id}/favorite`);
      loadFavGroups();
      if (favOnly && !next) loadAllProducts();
    } catch (e) {
      const back = (list) => list.map((x) => (x.id === p.id ? { ...x, is_favorite: !next } : x));
      setAllProducts(back);
      setProducts(back);
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  useEffect(() => { loadSurveys(); loadDicts(); }, []);
  useEffect(() => { loadProducts(); }, [loadProducts]);
  // 2026-02 · Debounce filter changes 300 ms so fast typing doesn't fire a
  // request per keystroke. Combined with reqSeqRef this cures the flaky search.
  useEffect(() => {
    if (view !== "products" || active) return;
    const h = setTimeout(() => { loadAllProducts(); }, 300);
    return () => clearTimeout(h);
  }, [view, active, loadAllProducts]);

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const clearSelection = () => setSelected(new Set());
  // 2026-06 · 全选 — selects every product currently loaded in the grid
  // (i.e. whatever the active filters + infinite scroll have produced).
  const allLoadedSelected = allProducts.length > 0 && allProducts.every((p) => selected.has(p.id));
  const toggleSelectAllLoaded = () => {
    setSelected(allLoadedSelected ? new Set() : new Set(allProducts.map((p) => p.id)));
  };

  // 2026-06 · 生成链接 — 30-day public photo wall of the picked products.
  const [shareLink, setShareLink] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareTitle, setShareTitle] = useState("");
  const createShareLink = async () => {
    if (selected.size === 0) return;
    setBatchBusy("share");
    try {
      const { data } = await api.post("/market/share", { ids: Array.from(selected), title: shareTitle.trim() });
      setShareLink(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBatchBusy(null); }
  };
  const closeShareDialog = () => { setShareOpen(false); setShareLink(null); setShareTitle(""); };

  // Batch actions: PDF export (any staff) + import to product library (admin/sales_manager).
  const [batchBusy, setBatchBusy] = useState(null); // null | "pdf" | "import"
  const runThumbPdf = async () => {
    if (selected.size === 0) return;
    setBatchBusy("pdf");
    try {
      const res = await api.post("/market/batch/thumbs-pdf", { ids: Array.from(selected) }, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `market-thumbs-${Date.now()}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBatchBusy(null); }
  };
  const runImportToLibrary = async () => {
    if (selected.size === 0) return;
    setBatchBusy("import");
    try {
      const { data } = await api.post("/market/batch/import-to-library", { ids: Array.from(selected) });
      toast.success(t("market.batchImportDone").replace("{created}", data.created).replace("{skipped}", data.skipped_existing));
      clearSelection();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBatchBusy(null); }
  };

  // 2026-10-11 · Bulk-delete selected survey products. Confirmation is
  // required and we show how many will actually be deleted vs skipped
  // (non-owners can only delete their own uploads).
  const bulkDeleteSelected = async () => {
    if (selected.size === 0) return;
    const ok = window.confirm(
      `确定要删除所选 ${selected.size} 条市场调查产品吗？\n此操作不可撤销。`
    );
    if (!ok) return;
    setBatchBusy("delete");
    try {
      const { data } = await api.post("/market/products/bulk-delete", { ids: Array.from(selected) });
      const parts = [`已删除 ${data.deleted}`];
      if (data.forbidden) parts.push(`无权限 ${data.forbidden}`);
      if (data.invalid) parts.push(`无效 ${data.invalid}`);
      toast.success(parts.join(" · "));
      clearSelection();
      loadAllProducts();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBatchBusy(null); }
  };

  // Product edit dialog state
  const [editing, setEditing] = useState(null);

  // Normalize a raw store name → strip location / branch suffixes so filter dropdown
  // groups "Big Lots - NJ", "Big Lots (Store #24)", "Big Lots/Newark" all into "Big Lots".
  const normalizeStore = (s) => {
    if (!s) return "";
    // Cut at first occurrence of "-", "(", "#", "/", "，", "," or a digit-run preceded by space.
    return s
      .replace(/\s*[-–—(#/，,].*$/u, "")
      .replace(/\s+\d{2,}.*$/u, "")
      .trim();
  };
  // Build the unique, normalized store list for the filter dropdown ONLY from
  // stores that actually have at least one product (via /dict-active/stores).
  // This excludes preset-but-unused dictionary entries the user added before
  // uploading photos (2026-08-01 fix).
  const normalizedStores = useMemo(() => {
    const set = new Map();
    activeStores.forEach((raw) => { const n = normalizeStore(raw); if (n && !set.has(n)) set.set(n, n); });
    return Array.from(set.values()).sort();
  }, [activeStores]);

  // FileMaker sync — admin only. Kicks off background pull, then polls status.
  const [fmSyncing, setFmSyncing] = useState(false);
  const [fmStatus, setFmStatus] = useState(null);
  // 2026-10-11 · Photo-repair job: re-downloads R2-missing photos from FM
  // for existing records (the regular sync skips existing rows so it can't
  // self-heal after an R2 data-loss event).
  const [repairing, setRepairing] = useState(false);
  const [repairStatus, setRepairStatus] = useState(null);

  // 2026-08 · Manual dedupe + R2 orphan sweep for admins.
  const runDedupe = async () => {
    if (!window.confirm("合并数据库中重复的 FM 照片记录？此操作不可撤销。")) return;
    try {
      const { data } = await api.post("/market/fm/dedupe");
      toast.success(`去重完成 · 合并 ${data.duplicates_removed} 条 · 分组 ${data.groups ?? "?"}`);
      loadAllProducts();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const runR2Sweep = async () => {
    try {
      // 2026-08 · R2 sweep runs in a background thread now (bucket has 160k+
      // objects, single request 502s at Cloudflare gateway ~100 s). Kick off
      // dry-run, poll status, then ask user to confirm real delete.
      await api.post("/market/fm/r2-sweep?dry_run=true");
      toast.info("R2 扫描已启动，请稍候…");
      const poll = () => new Promise((resolve, reject) => {
        const tick = async () => {
          try {
            const { data } = await api.get("/market/fm/r2-sweep/status");
            if (data.state === "running") setTimeout(tick, 3000);
            else if (data.state === "done") resolve(data);
            else reject(new Error(data.error || "扫描失败"));
          } catch (e) { reject(e); }
        };
        tick();
      });
      const scan = await poll();
      const ok = window.confirm(`R2 扫描完成 · 检查 ${scan.inspected} · 孤儿 ${scan.orphans_found}。立即删除？`);
      if (!ok) return;
      await api.post("/market/fm/r2-sweep?dry_run=false");
      toast.info("正在删除孤儿文件，请稍候…");
      const del = await poll();
      toast.success(`R2 清理完成 · 删除 ${del.deleted} 个`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || String(e)); }
  };
  const startFmPhotoRepair = async () => {
    const ok = window.confirm(
      "将扫描所有市场调查记录，对 R2 中缺失的图片从 FileMaker 容器重新下载。\n" +
      "任务在后台运行，可随时关闭页面。确认启动？"
    );
    if (!ok) return;
    try {
      setRepairing(true);
      await api.post("/market/fm/photos/repair");
      toast.success("图片修复已启动，后台运行中…");
      const poll = async () => {
        try {
          const { data } = await api.get("/market/fm/photos/repair/status");
          setRepairStatus(data);
          if (data.state === "running") setTimeout(poll, 3000);
          else {
            setRepairing(false);
            if (data.error) toast.error(`修复异常：${data.error}`);
            else toast.success(
              `图片修复完成 · 成功重取 ${data.repaired} · 原本正常 ${data.already_ok} · 失败 ${data.errors}`
            );
            loadAllProducts();
          }
        } catch { setRepairing(false); }
      };
      poll();
    } catch (e) {
      setRepairing(false);
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const startFmSync = async () => {
    try {
      setFmSyncing(true);
      await api.post("/market/fm/sync");
      toast.success(t("market.syncStarted"));
      const poll = async () => {
        try {
          const { data } = await api.get("/market/fm/sync/status");
          setFmStatus(data);
          if (data.state === "running") setTimeout(poll, 3000);
          else {
            setFmSyncing(false);
            if (data.state === "done") {
              toast.success(t("market.syncDoneToast").replace("{created}", data.created).replace("{existing}", data.skipped_existing).replace("{errors}", data.errors));
              loadAllProducts();
              loadSurveys();
            } else if (data.state === "error") toast.error(data.error || t("market.syncFailed"));
          }
        } catch { setFmSyncing(false); }
      };
      poll();
    } catch (e) {
      setFmSyncing(false);
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  // ---- CROSS-SURVEY PRODUCT LIST (DEFAULT LANDING) ------------------------
  if (!active && view === "products") {
    return (
      <div className="animate-fade-in-up" data-testid="market-products-page">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <h1 className="font-heading text-base font-bold tracking-tight">{t("market.title")}</h1>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={() => setView("surveys")} data-testid="market-view-surveys-btn" className="rounded-md h-9">
              <ListIcon size={14} className="mr-1" /> {t("market.surveyList")}
            </Button>
            {user?.role === "admin" && (
              <>
                <Button size="sm" variant="outline" onClick={startFmSync} disabled={fmSyncing} data-testid="market-fm-sync-btn" className="rounded-md h-9">
                  {fmSyncing ? <Loader2 size={14} className="mr-1 animate-spin" /> : <DownloadCloud size={14} className="mr-1" />}
                  {fmSyncing ? `${t("market.fmSyncing")} ${fmStatus?.processed || 0}/${fmStatus?.total || "?"}` : t("market.fmSync")}
                </Button>
                <Button size="sm" variant="outline" onClick={startFmPhotoRepair} disabled={repairing} data-testid="market-fm-photo-repair-btn" className="rounded-md h-9" title="从 FileMaker 容器重新下载 R2 已丢失的图片">
                  {repairing ? <Loader2 size={14} className="mr-1 animate-spin" /> : <ShieldCheck size={14} className="mr-1" />}
                  {repairing
                    ? `修复中 ${repairStatus?.processed || 0}/${repairStatus?.total || "?"} · 已修复 ${repairStatus?.repaired || 0}`
                    : "修复丢失图片"}
                </Button>
                <Button size="sm" variant="outline" onClick={runDedupe} data-testid="market-fm-dedupe-btn" className="rounded-md h-9">
                  <RefreshCw size={14} className="mr-1" /> 去重
                </Button>
                <Button size="sm" variant="outline" onClick={runR2Sweep} data-testid="market-fm-r2-sweep-btn" className="rounded-md h-9">
                  <Trash2 size={14} className="mr-1" /> R2 清理
                </Button>
                {fmSyncing && (
                  <Button size="sm" variant="outline" onClick={async () => { try { await api.post("/market/fm/sync/stop"); toast.info(t("market.stopSent")); } catch {} }} data-testid="market-fm-sync-stop-btn" className="rounded-md h-9">
                    <X size={14} className="mr-1" /> {t("market.stop")}
                  </Button>
                )}
              </>
            )}
            {canEdit && (
              <Button size="sm" onClick={() => setNewSurveyOpen(true)} data-testid="market-new-survey-btn" className="rounded-md h-9 bg-brand hover:bg-brand-hover">
                <Plus size={15} /> {t("market.newSurvey")}
              </Button>
            )}
          </div>
        </div>

        {/* Filter row — 门店/日期/品类/项目/用途/搜索
            2026-10-22 · 手机端收进底部抽屉，只保留搜索框 + 收藏 toggle + 筛选触发按钮；
                        桌面端保持原来的 8 列 grid 布局。 */}
        {/* 手机端：搜索 + 收藏 + 筛选触发（和桌面控件走不同分支） */}
        <div className="md:hidden flex items-center gap-2 mb-4">
          <Input
            value={globalFilters.search}
            onChange={(e) => setGlobalFilters((f) => ({ ...f, search: e.target.value }))}
            placeholder={t("market.searchName")}
            className="h-9 text-sm flex-1 min-w-0"
            data-testid="m-market-filter-search"
          />
          <Button
            variant={favOnly ? "default" : "outline"}
            size="sm"
            onClick={() => setFavOnly((v) => !v)}
            className={`h-9 shrink-0 ${favOnly ? "bg-amber-500 hover:bg-amber-600 text-white" : ""}`}
            data-testid="m-market-favorites-toggle"
          >
            <Star size={14} className={favOnly ? "fill-white" : ""} />
          </Button>
          <MobileFilterSheet
            activeCount={[globalFilters.store, globalFilters.date_from, globalFilters.date_to,
                          globalFilters.category, globalFilters.project, globalFilters.purpose].filter(Boolean).length}
            onReset={() => { setGlobalFilters((f) => ({ ...f, store: "", date_from: "", date_to: "", category: "", project: "", purpose: "" })); }}
            applyLabel={`${allProducts.length} ${t("products.recordsFound")}`}
            testIdPrefix="m-market-filter"
            title={t("products.filters")}
          >
            <div className="space-y-1">
              <p className="text-[11px] text-muted-foreground">{t("market.filterStore")}</p>
              <Select value={globalFilters.store || "__all__"} onValueChange={(v) => setGlobalFilters((f) => ({ ...f, store: v === "__all__" ? "" : v }))}>
                <SelectTrigger className="h-11 rounded-md text-sm w-full" data-testid="m-market-filter-store"><SelectValue placeholder={t("market.filterStore")} /></SelectTrigger>
                <SelectContent><SelectItem value="__all__">{t("market.filterStoreAll")}</SelectItem>{normalizedStores.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <p className="text-[11px] text-muted-foreground">{t("market.filterDateFrom")} – {t("market.filterDateTo")}</p>
              <div className="flex items-center gap-1.5">
                <Input type="date" value={globalFilters.date_from}
                  onChange={(e) => setGlobalFilters((f) => ({ ...f, date_from: e.target.value }))}
                  className="h-11 rounded-md font-mono text-xs" data-testid="m-market-filter-date-from" />
                <span className="text-muted-foreground text-xs">–</span>
                <Input type="date" value={globalFilters.date_to}
                  onChange={(e) => setGlobalFilters((f) => ({ ...f, date_to: e.target.value }))}
                  className="h-11 rounded-md font-mono text-xs" data-testid="m-market-filter-date-to" />
              </div>
            </div>
            <div className="space-y-1">
              <p className="text-[11px] text-muted-foreground">{t("market.filterCategory")}</p>
              <Select value={globalFilters.category || "__all__"} onValueChange={(v) => setGlobalFilters((f) => ({ ...f, category: v === "__all__" ? "" : v }))}>
                <SelectTrigger className="h-11 rounded-md text-sm w-full" data-testid="m-market-filter-category"><SelectValue placeholder={t("market.filterCategory")} /></SelectTrigger>
                <SelectContent><SelectItem value="__all__">{t("market.filterCategoryAll")}</SelectItem>{categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <p className="text-[11px] text-muted-foreground">{t("market.filterProject")}</p>
              <Select value={globalFilters.project || "__all__"} onValueChange={(v) => setGlobalFilters((f) => ({ ...f, project: v === "__all__" ? "" : v }))}>
                <SelectTrigger className="h-11 rounded-md text-sm w-full" data-testid="m-market-filter-project"><SelectValue placeholder={t("market.filterProject")} /></SelectTrigger>
                <SelectContent><SelectItem value="__all__">{t("market.filterProjectAll")}</SelectItem>{activeProjects.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <p className="text-[11px] text-muted-foreground">{t("market.filterPurpose")}</p>
              <Select value={globalFilters.purpose || "__all__"} onValueChange={(v) => setGlobalFilters((f) => ({ ...f, purpose: v === "__all__" ? "" : v }))}>
                <SelectTrigger className="h-11 rounded-md text-sm w-full" data-testid="m-market-filter-purpose"><SelectValue placeholder={t("market.filterPurpose")} /></SelectTrigger>
                <SelectContent><SelectItem value="__all__">{t("market.filterPurposeAll")}</SelectItem>{PHOTO_TYPES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Button
              variant="outline"
              onClick={() => { setGlobalFilters({ store: "", date_from: "", date_to: "", category: "", project: "", purpose: "", search: "" }); setFavOnly(false); setActiveFavGroup(""); clearSelection(); }}
              data-testid="m-market-filter-reset-all"
              className="w-full h-11 rounded-md">
              <RefreshCw size={13} className="mr-1" /> {t("market.reset")}
            </Button>
          </MobileFilterSheet>
        </div>

        {/* 桌面端：原来的 8 列 grid 筛选 */}
        <div className="hidden md:grid grid-cols-2 md:grid-cols-8 gap-2 mb-4">
          <Select value={globalFilters.store || "__all__"} onValueChange={(v) => setGlobalFilters((f) => ({ ...f, store: v === "__all__" ? "" : v }))}>
            <SelectTrigger className="h-8 text-xs" data-testid="market-global-filter-store"><SelectValue placeholder={t("market.filterStore")} /></SelectTrigger>
            <SelectContent><SelectItem value="__all__">{t("market.filterStoreAll")}</SelectItem>{normalizedStores.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}</SelectContent>
          </Select>
          <Input
            type="date"
            value={globalFilters.date_from}
            onChange={(e) => setGlobalFilters((f) => ({ ...f, date_from: e.target.value }))}
            className="h-8 text-xs"
            placeholder={t("market.filterDateFrom")}
            data-testid="market-global-filter-date-from"
          />
          <Input
            type="date"
            value={globalFilters.date_to}
            onChange={(e) => setGlobalFilters((f) => ({ ...f, date_to: e.target.value }))}
            className="h-8 text-xs"
            placeholder={t("market.filterDateTo")}
            data-testid="market-global-filter-date-to"
          />
          <Select value={globalFilters.category || "__all__"} onValueChange={(v) => setGlobalFilters((f) => ({ ...f, category: v === "__all__" ? "" : v }))}>
            <SelectTrigger className="h-8 text-xs" data-testid="market-global-filter-category"><SelectValue placeholder={t("market.filterCategory")} /></SelectTrigger>
            <SelectContent><SelectItem value="__all__">{t("market.filterCategoryAll")}</SelectItem>{categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={globalFilters.project || "__all__"} onValueChange={(v) => setGlobalFilters((f) => ({ ...f, project: v === "__all__" ? "" : v }))}>
            <SelectTrigger className="h-8 text-xs" data-testid="market-global-filter-project"><SelectValue placeholder={t("market.filterProject")} /></SelectTrigger>
            <SelectContent><SelectItem value="__all__">{t("market.filterProjectAll")}</SelectItem>{activeProjects.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={globalFilters.purpose || "__all__"} onValueChange={(v) => setGlobalFilters((f) => ({ ...f, purpose: v === "__all__" ? "" : v }))}>
            <SelectTrigger className="h-8 text-xs" data-testid="market-global-filter-purpose"><SelectValue placeholder={t("market.filterPurpose")} /></SelectTrigger>
            <SelectContent><SelectItem value="__all__">{t("market.filterPurposeAll")}</SelectItem>{PHOTO_TYPES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
          </Select>
          <Input
            value={globalFilters.search}
            onChange={(e) => setGlobalFilters((f) => ({ ...f, search: e.target.value }))}
            placeholder={t("market.searchName")}
            className="h-8 text-xs"
            data-testid="market-global-filter-search"
          />
          <Button
            variant={favOnly ? "default" : "outline"}
            size="sm"
            onClick={() => setFavOnly((v) => !v)}
            className={`h-8 text-xs ${favOnly ? "bg-amber-500 hover:bg-amber-600 text-white" : ""}`}
            data-testid="market-favorites-toggle"
          >
            <Star size={13} className={favOnly ? "fill-white" : ""} /> {t("market.myFavorites")}
          </Button>
          <Button variant="outline" size="sm" onClick={() => { setGlobalFilters({ store: "", date_from: "", date_to: "", category: "", project: "", purpose: "", search: "" }); setFavOnly(false); setActiveFavGroup(""); clearSelection(); }} className="h-8 text-xs" data-testid="market-global-filter-reset">
            <RefreshCw size={12} className="mr-1" /> {t("market.reset")}
          </Button>
        </div>

        {/* 2026-06 · 收藏分组筛选条 — 仅在「我的收藏」开启时出现 */}
        {favOnly && (
          <div className="mb-3">
            <FavGroupBar
              groups={favGroups}
              meta={favMeta}
              activeGroup={activeFavGroup}
              onPick={setActiveFavGroup}
              onChanged={() => { loadFavGroups(); loadAllProducts(); }}
            />
          </div>
        )}

        {/* 2026-06 · 全选 (selects everything currently loaded in the grid) */}
        {allProducts.length > 0 && (
          <div className="flex items-center gap-2 mb-3">
            <Button size="sm" variant="outline" onClick={toggleSelectAllLoaded}
              className="h-7 text-xs" data-testid="market-select-all-btn">
              <CheckSquare size={12} className="mr-1" />
              {allLoadedSelected ? t("market.unselectAll") : t("market.selectAll").replace("{n}", String(allProducts.length))}
            </Button>
          </div>
        )}

        {/* Selection bar · sticky so it stays visible while the user scrolls
            through 996+ rows to build the batch. */}
        {selected.size > 0 && (
          <div className="sticky top-14 z-30 flex items-center justify-between mb-3 px-3 py-2 rounded-md bg-brand/10 border border-brand/40 text-xs flex-wrap gap-2 shadow-sm" data-testid="market-selection-bar">
            <span data-testid="market-selection-count">{t("market.selected")} <b>{selected.size}</b> · <span className="text-muted-foreground">{t("market.selectionTtl")}</span></span>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={() => setShareOpen(true)} disabled={batchBusy} data-testid="market-share-link-btn" className="h-7 text-xs">
                {batchBusy === "share" ? <Loader2 size={12} className="mr-1 animate-spin" /> : <Link2 size={12} className="mr-1" />}
                {t("market.shareLink")}
              </Button>
              <Button size="sm" variant="outline" onClick={runThumbPdf} disabled={batchBusy} data-testid="market-batch-pdf-btn" className="h-7 text-xs">
                {batchBusy === "pdf" ? <Loader2 size={12} className="mr-1 animate-spin" /> : <DownloadCloud size={12} className="mr-1" />}
                {batchBusy === "pdf" ? t("market.batchPdfGenerating") : t("market.batchPdf")}
              </Button>
              {(user?.role === "admin" || user?.role === "sales_manager") && (
                <Button size="sm" variant="outline" onClick={runImportToLibrary} disabled={batchBusy} data-testid="market-batch-import-btn" className="h-7 text-xs">
                  {batchBusy === "import" ? <Loader2 size={12} className="mr-1 animate-spin" /> : <Check size={12} className="mr-1" />}
                  {t("market.batchImport")}
                </Button>
              )}
              <Button
                size="sm"
                variant={showOnlySelected ? "default" : "outline"}
                onClick={() => setShowOnlySelected((v) => !v)}
                data-testid="market-filter-selected-btn"
                className={`h-7 text-xs ${showOnlySelected ? "bg-brand text-brand-foreground hover:bg-brand/90" : ""}`}
                title="只显示已选中的产品"
              >
                <Filter size={12} className="mr-1" />
                {showOnlySelected ? `只看已选 (${selected.size})` : "筛选已选"}
              </Button>
              {canEdit && user?.role === "admin" && (
                <Button
                  size="sm" variant="outline"
                  onClick={bulkDeleteSelected}
                  disabled={batchBusy === "delete"}
                  data-testid="market-bulk-delete-btn"
                  className="h-7 text-xs text-red-600 border-red-200 hover:bg-red-50"
                  title="删除所选产品（仅管理员）"
                >
                  {batchBusy === "delete"
                    ? <Loader2 size={12} className="mr-1 animate-spin" />
                    : <Trash2 size={12} className="mr-1" />}
                  删除所选
                </Button>
              )}
              <Button size="sm" variant="ghost" onClick={clearSelection} data-testid="market-selection-clear" className="h-7 text-xs text-destructive hover:text-destructive hover:bg-destructive/10">
                <X size={12} className="mr-1" /> {t("market.clearSelection")}
              </Button>
            </div>
          </div>
        )}

        {/* Product grid */}
        {allProducts.length === 0 ? (
          <div className="py-24 text-center text-sm text-muted-foreground border border-dashed border-border rounded-lg" data-testid="market-empty">
            {t("market.emptyProducts")}
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
            {/* 2026-02 · Visually merge rows that came from the SAME FileMaker
                record (same fm_record_id) — FM stores up to 4 photo fields per
                record and our sync creates one survey_products doc per field,
                which the user saw as "same product appearing multiple times"
                in the grid. Rows without fm_record_id (WebApp uploads) stay as
                distinct cards. `_group_extras` counts how many were folded. */}
            {(() => {
              // 2026-10-11 · Admin "只看已选" toggle filters to selected IDs.
              const source = showOnlySelected
                ? allProducts.filter((p) => selected.has(p.id))
                : allProducts;
              const seenFm = new Map();
              const list = [];
              for (const p of source) {
                if (p.fm_record_id) {
                  const k = `${p.fm_record_id}::${p.survey_id || ""}`;
                  const existing = seenFm.get(k);
                  if (existing) {
                    existing._group_extras = (existing._group_extras || 0) + 1;
                    continue;
                  }
                  const copy = { ...p, _group_extras: 0 };
                  seenFm.set(k, copy);
                  list.push(copy);
                } else {
                  list.push(p);
                }
              }
              return list;
            })().map((p) => {
              const isSel = selected.has(p.id);
              return (
                <div
                  key={p.id}
                  className={`relative bg-card border rounded-lg overflow-hidden transition-all cursor-pointer hover:border-brand ${isSel ? "border-brand ring-2 ring-brand/40" : "border-border"}`}
                  data-testid={`market-product-${p.id}`}
                  onClick={() => setEditing(p)}
                >
                  {/* Checkbox top-left — stopPropagation so it doesn't open the dialog */}
                  <div className="absolute top-1.5 left-1.5 z-10 bg-background/80 backdrop-blur rounded p-0.5 shadow-sm" onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      checked={isSel}
                      onCheckedChange={() => toggleSelect(p.id)}
                      data-testid={`market-product-check-${p.id}`}
                    />
                  </div>
                  <button
                    type="button"
                    className="absolute top-1.5 right-1.5 z-20 rounded p-0.5 bg-background/80 backdrop-blur shadow-sm"
                    title={t("market.myFavorites")}
                    data-testid={`market-product-fav-${p.id}`}
                    onClick={(e) => { e.stopPropagation(); toggleFavorite(p); }}
                  >
                    <Star size={14} className={p.is_favorite ? "fill-amber-400 text-amber-500" : "text-muted-foreground"} />
                  </button>
                  {p.is_favorite && (
                    <FavGroupPicker product={p} groups={favGroups} onSaved={applyGroups} />
                  )}
                  <div className="aspect-square bg-muted overflow-hidden">
                    <SurveyPhotoThumb photo={p.photos?.[0]} placeholder={t("market.noImage")} />
                    {/* 2026-02 · When we merged sibling FM-photo rows into this
                        card, show a chip so the user knows there are extra
                        photo variants to browse via prev/next in the edit
                        dialog. */}
                    {p._group_extras > 0 && (
                      <div className="absolute top-1.5 right-8 z-10 px-1.5 py-0.5 rounded bg-brand/90 text-white text-[10px] font-semibold shadow-sm" data-testid={`market-product-group-${p.id}`}>
                        +{p._group_extras}
                      </div>
                    )}
                  </div>
                  <div className="p-2.5 space-y-1">
                    <p className="text-xs font-mono font-semibold truncate">{p.item_number || "—"}</p>
                    <p className="text-[10px] text-muted-foreground truncate">{p.description || p.category || "—"}</p>
                    <div className="flex items-center justify-between text-[10px] text-muted-foreground">
                      <span className="truncate">{p.store || "—"}</span>
                      <span className="font-mono">{p.survey_date || ""}</span>
                    </div>
                    <p className="text-[10px] text-muted-foreground">{p.photos?.length || 0} {t("market.photoCount")}</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {/* Pagination footer — server-side pagination: `total` comes from the
            DB (via count_documents) so 5k+ FM imports report their true size
            instead of the 2000-row client cap we had before. */}
        {(totalProducts > 0 || allProducts.length > 0) && (
          <div className="mt-4 flex items-center justify-center gap-3 text-xs text-muted-foreground" data-testid="market-pagination">
            <span data-testid="market-pagination-count">{t("market.showing").replace("{shown}", allProducts.length).replace("{total}", totalProducts)}</span>
            {allProducts.length < totalProducts && (
              <Button size="sm" variant="outline" onClick={() => loadAllProducts({ append: true })} disabled={pageLoading} data-testid="market-load-more-btn" className="h-8">
                {pageLoading ? <Loader2 size={12} className="mr-1 animate-spin" /> : null}
                {t("market.loadMore")}
              </Button>
            )}
          </div>
        )}
        <NewSurveyDialog open={newSurveyOpen} onOpenChange={setNewSurveyOpen} stores={stores} onDictReload={loadDicts} onCreated={(s) => { setActive(s); loadSurveys(); }} />
        <EditProductDialog
          product={editing}
          siblings={allProducts}
          onClose={() => setEditing(null)}
          onNavigate={(next) => setEditing(next)}
          projects={projects}
          onDictReload={loadDicts}
          onSaved={(updated) => {
            if (updated.__deleted) {
              setAllProducts((prev) => prev.filter((x) => x.id !== updated.id));
              setTotalProducts((t) => Math.max(0, t - 1));
              setEditing(null);
            } else {
              // Keep the dialog open on the just-saved record so users can
              // review the update, then use prev/next to move on. Previously
              // the dialog closed on every save which broke the new bulk-edit
              // workflow (user spec 2026-02).
              setAllProducts((prev) => prev.map((x) => (x.id === updated.id ? { ...x, ...updated } : x)));
              setEditing((prev) => (prev && prev.id === updated.id ? { ...prev, ...updated } : prev));
            }
          }}
          categories={categories}
          purposes={purposes}
        />

      {/* 2026-06 · 生成链接 result — public 30-day photo wall */}
        <Dialog open={shareOpen} onOpenChange={(o) => !o && closeShareDialog()}>
          <DialogContent className="max-w-lg" data-testid="market-share-dialog">
            <DialogHeader>
              <DialogTitle className="text-base flex items-center gap-2">
                <Link2 size={15} /> {shareLink ? t("market.shareDialogTitle") : t("market.shareSetupTitle")}
              </DialogTitle>
            </DialogHeader>

            {!shareLink ? (
              <>
                <p className="text-xs text-muted-foreground">
                  {t("market.shareSetupHint").replace("{n}", String(selected.size))}
                </p>
                <div>
                  <label className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">
                    {t("market.shareTitleLabel")}
                  </label>
                  <Input
                    value={shareTitle}
                    autoFocus
                    onChange={(e) => setShareTitle(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" && !batchBusy) createShareLink(); }}
                    placeholder={t("market.shareTitlePlaceholder")}
                    className="h-9 text-sm mt-1"
                    data-testid="market-share-title-input"
                  />
                </div>
                <DialogFooter>
                  <Button variant="outline" size="sm" onClick={closeShareDialog}>{t("market.cancel")}</Button>
                  <Button size="sm" onClick={createShareLink} disabled={batchBusy} data-testid="market-share-create-btn">
                    {batchBusy === "share" ? <Loader2 size={13} className="mr-1 animate-spin" /> : <Link2 size={13} className="mr-1" />}
                    {t("market.shareCreate")}
                  </Button>
                </DialogFooter>
              </>
            ) : (
              <>
                <p className="text-xs text-muted-foreground">
                  {t("market.shareDialogHint")
                    .replace("{n}", String(shareLink?.count ?? 0))
                    .replace("{date}", (shareLink?.expires_at || "").slice(0, 10))}
                </p>
                <div className="flex items-center gap-2">
                  <Input readOnly value={shareLink?.url || ""} className="h-9 text-xs font-mono" data-testid="market-share-url" />
                  <Button size="sm" className="h-9 shrink-0" data-testid="market-share-copy"
                    onClick={() => {
                      navigator.clipboard?.writeText(shareLink?.url || "");
                      toast.success(t("market.shareCopied"));
                    }}>
                    <Copy size={13} className="mr-1" /> {t("market.shareCopy")}
                  </Button>
                </div>
                <DialogFooter>
                  <Button variant="outline" size="sm" onClick={() => window.open(shareLink?.url, "_blank")} data-testid="market-share-open">
                    {t("market.shareOpen")}
                  </Button>
                </DialogFooter>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // ---- SURVEY LIST VIEW ---------------------------------------------------
  if (!active) {
    return (
      <div className="animate-fade-in-up" data-testid="market-survey-list">
        <div className="flex items-center justify-between mb-3">
          <h1 className="font-heading text-base font-bold tracking-tight">{t("market.title")}</h1>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={() => setView("products")} data-testid="market-view-products-btn" className="rounded-md h-9">
              <Grid3x3 size={14} className="mr-1" /> {t("market.productList")}
            </Button>
            {canEdit && (
              <Button size="sm" onClick={() => setNewSurveyOpen(true)} data-testid="market-new-survey-btn" className="rounded-md h-9 bg-brand hover:bg-brand-hover">
                <Plus size={15} /> {t("market.newSurvey")}
              </Button>
            )}
          </div>
        </div>
        {surveys.length === 0 ? (
          <div className="py-24 text-center text-sm text-muted-foreground border border-dashed border-border rounded-lg">
            {t("market.emptyList")}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {surveys.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => setActive(s)}
                className="text-left bg-card border border-border rounded-lg p-4 hover:border-primary hover:shadow-sm transition-all"
                data-testid={`market-survey-card-${s.id}`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <StoreIcon size={14} className="text-brand" />
                  <h3 className="font-heading text-base font-bold truncate">{s.store}</h3>
                </div>
                <p className="text-xs text-muted-foreground font-mono">{s.survey_date} · {s.surveyor || "—"}</p>
                <p className="text-xs mt-2">{s.product_count} {t("market.productCount")}</p>
              </button>
            ))}
          </div>
        )}
        <NewSurveyDialog open={newSurveyOpen} onOpenChange={setNewSurveyOpen} stores={stores} onDictReload={loadDicts} onCreated={(s) => { setActive(s); loadSurveys(); }} />
      </div>
    );
  }

  // ---- SURVEY DETAIL VIEW -------------------------------------------------
  return (
    <div className="animate-fade-in-up" data-testid="market-survey-detail">
      <div className="mb-3 flex items-center justify-between rounded-md border border-border bg-brand/5 px-3 py-2">
        <div className="flex items-center gap-2 text-xs">
          <StoreIcon size={13} className="text-brand" />
          <span>{t("market.selectingFor")}</span>
          <b>{active.store}</b>
          <span className="font-mono text-muted-foreground">· {active.survey_date}</span>
        </div>
        <button onClick={() => setActive(null)} className="text-xs text-primary hover:underline" data-testid="market-back-to-list">
          {t("market.backToList")}
        </button>
      </div>

      <div className="flex items-center justify-between mb-4">
        <h1 className="font-heading text-base font-bold tracking-tight">{t("market.title")}</h1>
      </div>

      {/* Filter row */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-2 mb-4">
        <Select value={filters.category || "__all__"} onValueChange={(v) => setFilters((f) => ({ ...f, category: v === "__all__" ? "" : v }))}>
          <SelectTrigger className="h-8 text-xs" data-testid="market-filter-category"><SelectValue placeholder={t("market.filterCategory")} /></SelectTrigger>
          <SelectContent><SelectItem value="__all__">{t("market.filterCategoryAll")}</SelectItem>{categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={filters.project || "__all__"} onValueChange={(v) => setFilters((f) => ({ ...f, project: v === "__all__" ? "" : v }))}>
          <SelectTrigger className="h-8 text-xs" data-testid="market-filter-project"><SelectValue placeholder={t("market.filterProject")} /></SelectTrigger>
          <SelectContent><SelectItem value="__all__">{t("market.filterProjectAll")}</SelectItem>{activeProjects.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={filters.purpose || "__all__"} onValueChange={(v) => setFilters((f) => ({ ...f, purpose: v === "__all__" ? "" : v }))}>
          <SelectTrigger className="h-8 text-xs" data-testid="market-filter-purpose"><SelectValue placeholder={t("market.filterPurpose")} /></SelectTrigger>
          <SelectContent><SelectItem value="__all__">{t("market.filterPurposeAll")}</SelectItem>{PHOTO_TYPES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
        </Select>
        <Input value={filters.search} onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))} placeholder={t("market.searchName")} className="h-8 text-xs md:col-span-2" data-testid="market-filter-search" />
        <Button variant="outline" size="sm" onClick={() => setFilters({ category: "", project: "", purpose: "", search: "" })} className="h-8 text-xs" data-testid="market-filter-reset">
          <RefreshCw size={12} className="mr-1" /> {t("market.reset")}
        </Button>
      </div>

      {/* Action row */}
      {canEdit && (
        <div className="flex items-center gap-2 mb-4">
          <Button size="sm" onClick={() => setUploadOpen(true)} data-testid="market-upload-btn" className="rounded-md h-9 bg-brand hover:bg-brand-hover">
            <Upload size={14} /> {t("market.importPhotos")}
          </Button>
        </div>
      )}

      {/* Product grid */}
      {products.length === 0 ? (
        <div className="py-16 text-center text-xs text-muted-foreground">{t("market.emptySurveyProducts")}</div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
          {products.map((p) => {
            const isSel = selected.has(p.id);
            return (
            <div
              key={p.id}
              className={`relative bg-card border rounded-lg overflow-hidden transition-all cursor-pointer hover:border-brand ${isSel ? "border-brand ring-2 ring-brand/40" : "border-border"}`}
              data-testid={`market-product-${p.id}`}
              onClick={() => setEditing({ ...p, store: active.store, survey_date: active.survey_date })}
            >
              <div className="absolute top-1.5 left-1.5 z-10 bg-background/80 backdrop-blur rounded p-0.5 shadow-sm" onClick={(e) => e.stopPropagation()}>
                <Checkbox checked={isSel} onCheckedChange={() => toggleSelect(p.id)} data-testid={`market-product-check-${p.id}`} />
              </div>
              <div className="aspect-square bg-muted overflow-hidden">
                <SurveyPhotoThumb photo={p.photos?.[0]} placeholder={t("market.noImage")} />
              </div>
              <div className="p-2.5 space-y-1">
                <p className="text-xs font-mono font-semibold truncate">{p.item_number || "—"}</p>
                <p className="text-[10px] text-muted-foreground truncate">{p.description || p.category || "—"}</p>
                <p className="text-[10px] text-muted-foreground">{p.photos?.length || 0} {t("market.photoCount")}</p>
              </div>
            </div>
            );
          })}
        </div>
      )}

      <UploadPhotosDialog
        open={uploadOpen}
        onOpenChange={setUploadOpen}
        survey={active}
        stores={stores} projects={projects} purposes={purposes} categories={categories}
        onDictReload={loadDicts}
        onUploaded={() => { loadProducts(); loadSurveys(); }}
      />

      {/* 2026-02 · Edit dialog wired into the survey-scoped grid too. Same
          dialog used by the global product grid — prev/next paging walks
          through THIS survey's products (siblings=products), so clicking a
          card from the survey drill-down opens the same edit UX. */}
      <EditProductDialog
        product={editing}
        siblings={products.map((p) => ({ ...p, store: active.store, survey_date: active.survey_date }))}
        onClose={() => setEditing(null)}
        onNavigate={(next) => setEditing(next)}
        projects={projects}
        onDictReload={loadDicts}
        onSaved={(updated) => {
          if (updated.__deleted) {
            setProducts((prev) => prev.filter((x) => x.id !== updated.id));
            setEditing(null);
          } else {
            setProducts((prev) => prev.map((x) => (x.id === updated.id ? { ...x, ...updated } : x)));
            setEditing((prev) => (prev && prev.id === updated.id ? { ...prev, ...updated } : prev));
          }
        }}
        categories={categories}
        purposes={purposes}
      />

    </div>
  );
}

// ============================================================================
// Sub: New Survey Dialog
// ============================================================================
function NewSurveyDialog({ open, onOpenChange, stores, onDictReload, onCreated }) {
  const { t } = useLanguage();
  const [store, setStore] = useState("");
  const [customStore, setCustomStore] = useState("");
  const [surveyDate, setSurveyDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);
  const save = async () => {
    const s = (store === "__new__" ? customStore : store).trim();
    if (!s) { toast.error(t("market.newSurveyStorePlaceholder")); return; }
    setBusy(true);
    try {
      if (store === "__new__") {
        await api.post("/market/dict/stores", { name: s });
        await onDictReload();
      }
      const { data } = await api.post("/market/surveys", { store: s, survey_date: surveyDate });
      toast.success(t("market.newSurveyCreated"));
      onCreated?.(data);
      onOpenChange(false);
      setStore(""); setCustomStore("");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm" data-testid="market-new-survey-dialog">
        <DialogHeader><DialogTitle>{t("market.newSurveyTitle")}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">{t("market.newSurveyStore")}</label>
            <Select value={store} onValueChange={setStore}>
              <SelectTrigger data-testid="market-new-store-select"><SelectValue placeholder={t("market.newSurveyStorePlaceholder")} /></SelectTrigger>
              <SelectContent>
                {stores.map((s) => <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>)}
                <SelectItem value="__new__">+ {t("market.newSurveyStore")}</SelectItem>
              </SelectContent>
            </Select>
            {store === "__new__" && (
              <Input autoFocus value={customStore} onChange={(e) => setCustomStore(e.target.value)} placeholder={t("market.newSurveyStorePlaceholder")} className="mt-2 h-8 text-xs" data-testid="market-new-store-input" />
            )}
          </div>
          <div>
            <label className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">{t("market.newSurveyDate")}</label>
            <Input type="date" value={surveyDate} onChange={(e) => setSurveyDate(e.target.value)} className="h-8 text-xs" data-testid="market-new-date" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t("market.cancel")}</Button>
          <Button onClick={save} disabled={busy} data-testid="market-new-survey-save">{t("market.save")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// Sub: Upload Photos Dialog
// ============================================================================
function UploadPhotosDialog({ open, onOpenChange, survey, stores, projects, purposes, categories, onDictReload, onUploaded }) {
  const { t } = useLanguage();
  const inputRef = useRef(null);
  const [files, setFiles] = useState([]); // each: {id, file, status: queued|uploading|done|failed, progress, photoUrl, error}
  const [busy, setBusy] = useState(false);
  // 2026-08-01 · Upload flow simplified — photo type & project no longer asked
  // per upload batch (user spec). Only category is preset here; photo type is
  // filled later via the edit dialog.
  const [preset, setPreset] = useState({ category: "" });

  useEffect(() => {
    if (!open) {
      setFiles([]); setBusy(false); setPreset({ category: "" });
    }
  }, [open]);

  const addFiles = (list) => {
    const arr = [...list].filter((f) =>
      /^image\//.test(f.type) || /\.hei[cf]$/i.test(f.name || "")
    );
    if (files.length + arr.length > 300) { toast.error(t("market.uploadTooMany")); return; }
    const wrapped = arr.map((f) => ({ id: crypto.randomUUID(), file: f, status: "queued", progress: 0 }));
    setFiles((prev) => [...prev, ...wrapped]);
  };

  // 2026-08 · Per-file parallel upload with concurrency limit 4.
  // Each file → one small POST → server returns fast, thumb generated async.
  const uploadOne = async (entry) => {
    setFiles((cur) => cur.map((f) => (f.id === entry.id ? { ...f, status: "uploading", progress: 10 } : f)));
    try {
      const compressed = await maybeCompressImage(entry.file, { targetBytes: 3 * 1024 * 1024 });
      setFiles((cur) => cur.map((f) => (f.id === entry.id ? { ...f, progress: 40 } : f)));
      const fd = new FormData();
      fd.append("survey_id", survey.id);
      fd.append("store", survey.store);
      fd.append("survey_date", survey.survey_date);
      fd.append("category", preset.category || "");
      fd.append("project", "");
      fd.append("purpose", "");
      fd.append("file", compressed, compressed.name);
      const { data } = await api.post("/market/photos/single", fd, {
        timeout: 60000,
        onUploadProgress: (e) => {
          if (!e.total) return;
          const pct = 40 + Math.round((e.loaded / e.total) * 55);
          setFiles((cur) => cur.map((f) => (f.id === entry.id ? { ...f, progress: pct } : f)));
        },
      });
      setFiles((cur) => cur.map((f) => (f.id === entry.id ? { ...f, status: "done", progress: 100, photoUrl: data.photo_url } : f)));
    } catch (e) {
      setFiles((cur) => cur.map((f) => (f.id === entry.id ? { ...f, status: "failed", error: formatApiErrorDetail(e.response?.data?.detail) || t("market.uploadFailed") } : f)));
    }
  };

  const runQueue = async () => {
    setBusy(true);
    try {
      const queue = files.filter((f) => f.status !== "done");
      const LIMIT = 4;
      let idx = 0;
      const worker = async () => {
        while (idx < queue.length) {
          const my = queue[idx++];
          await uploadOne(my);
        }
      };
      await Promise.all(Array.from({ length: Math.min(LIMIT, queue.length) }, worker));
      setFiles((cur) => {
        const ok = cur.filter((f) => f.status === "done").length;
        const failed = cur.filter((f) => f.status === "failed").length;
        if (failed > 0) toast.success(`${t("market.uploadDone")} ${ok} · ${t("market.uploadFailed")} ${failed}`);
        else toast.success(`${t("market.uploadDone")} ${ok}`);
        return cur;
      });
      onUploaded?.();
      setTimeout(() => onUploaded?.(), 3000);
      setTimeout(() => onUploaded?.(), 8000);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const retryFailed = async () => {
    const failed = files.filter((f) => f.status === "failed");
    setBusy(true);
    try {
      const LIMIT = 4;
      let idx = 0;
      const worker = async () => {
        while (idx < failed.length) {
          const my = failed[idx++];
          await uploadOne(my);
        }
      };
      await Promise.all(Array.from({ length: Math.min(LIMIT, failed.length) }, worker));
    } finally { setBusy(false); }
  };

  const doneCount = files.filter((f) => f.status === "done").length;
  const failedCount = files.filter((f) => f.status === "failed").length;

  return (
    <Dialog open={open} onOpenChange={(v) => !busy && onOpenChange(v)}>
      <DialogContent className="max-w-3xl" data-testid="market-upload-dialog">
        <DialogHeader><DialogTitle>{t("market.uploadTitle")}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-3">
            <input ref={inputRef} type="file" accept="image/*,.heic,.heif" multiple hidden onChange={(e) => addFiles(e.target.files)} data-testid="market-file-input" />
            <button type="button" onClick={() => inputRef.current?.click()} disabled={busy} className="w-full h-40 border-2 border-dashed border-border rounded-md flex flex-col items-center justify-center gap-2 text-xs text-muted-foreground hover:border-brand hover:bg-brand/5 disabled:opacity-50" data-testid="market-dropzone">
              <Upload size={20} />
              <span><b>{t("market.uploadClickToPick")}</b></span>
              <span className="text-[10px]">{t("market.uploadHint")}</span>
            </button>
            <div className="text-xs flex items-center gap-3">
              <span>{t("market.uploadPicked")} {files.length} · {t("market.uploadSucceeded")} <b className="text-emerald-600">{doneCount}</b>{failedCount > 0 && <> · {t("market.uploadFailed")} <b className="text-destructive">{failedCount}</b></>}</span>
              {!busy && files.length > 0 && <button onClick={() => setFiles([])} className="text-primary hover:underline" data-testid="market-clear-files">{t("market.uploadClear")}</button>}
              {failedCount > 0 && !busy && <button onClick={retryFailed} className="text-primary hover:underline" data-testid="market-retry-failed">{t("market.uploadRetryFailed")}</button>}
            </div>
            <div className="grid grid-cols-6 gap-1.5 max-h-40 overflow-y-auto">
              {files.map((entry) => (
                <div key={entry.id} className="relative aspect-square bg-muted rounded border border-border overflow-hidden">
                  {/^image\//.test(entry.file.type) && !/\.hei[cf]$/i.test(entry.file.name || "") ? (
                    <img src={URL.createObjectURL(entry.file)} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-[9px] text-muted-foreground p-1 text-center">{entry.file.name.slice(-12)}</div>
                  )}
                  {/* Status overlay */}
                  {entry.status === "uploading" && (
                    <div className="absolute inset-0 bg-black/60 flex flex-col items-center justify-center text-white text-[10px]">
                      <Loader2 size={12} className="animate-spin mb-0.5" />
                      {entry.progress}%
                    </div>
                  )}
                  {entry.status === "done" && (
                    <div className="absolute top-0.5 right-0.5 bg-emerald-500 rounded-full w-4 h-4 flex items-center justify-center text-white">
                      <Check size={10} strokeWidth={3} />
                    </div>
                  )}
                  {entry.status === "failed" && (
                    <div className="absolute inset-0 bg-destructive/70 flex items-center justify-center text-white text-[9px] p-1 text-center" title={entry.error}>{t("market.uploadFailed")}</div>
                  )}
                  {entry.status === "queued" && !busy && (
                    <button onClick={() => setFiles((p) => p.filter((f) => f.id !== entry.id))} className="absolute top-0.5 right-0.5 bg-black/60 text-white rounded-full w-4 h-4 flex items-center justify-center"><X size={10} /></button>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-3">
            <p className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">{t("market.uploadPreset")}</p>
            <div><label className="text-xs">{t("market.uploadStore")} <span className="text-destructive">*</span></label><Input value={survey.store} disabled className="h-8 text-xs" /></div>
            <div><label className="text-xs">{t("market.uploadDate")} <span className="text-destructive">*</span></label><Input value={survey.survey_date} disabled className="h-8 text-xs" /></div>
            <div>
              <label className="text-xs">{t("market.editCategory")}</label>
              <Select value={preset.category || "__none__"} onValueChange={(v) => setPreset((p) => ({ ...p, category: v === "__none__" ? "" : v }))}>
                <SelectTrigger className="h-8 text-xs" data-testid="market-preset-category"><SelectValue placeholder={t("market.uploadCategorySelect")} /></SelectTrigger>
                <SelectContent><SelectItem value="__none__">{t("market.editCategoryNone")}</SelectItem>{categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>{t("market.uploadClose")}</Button>
          <Button onClick={runQueue} disabled={busy || files.length === 0 || doneCount === files.length} data-testid="market-upload-start">
            {busy ? <Loader2 size={14} className="animate-spin mr-1" /> : <Upload size={14} className="mr-1" />}
            {busy ? `${t("market.fmSyncing")}…` : t("market.uploadStart")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ============================================================================
// Sub: Product Edit Dialog
// Photo shown at ~30% larger than the list-card thumbnail (list cards are
// aspect-square within a 5-col grid, ~270 px on 1440 px wide viewports;
// dialog image lands at ~360 px = +33%).
// ============================================================================
function EditProductDialog({ product, siblings, onClose, onNavigate, onSaved, categories, projects, purposes, onDictReload }) {
  const { t } = useLanguage();
  const [form, setForm] = useState({ item_number: "", description: "", category: "", project: "", notes: "", photo_type: "", country_of_origin: "", supplier: "" });
  const [busy, setBusy] = useState(false);
  // Inline "+ new project" flow (mirrors the store picker in NewSurveyDialog).
  // Value "__new__" flips the field into a text input; on save we POST to
  // /market/dict/projects then reload the dict so the new name shows up in
  // future dropdowns without a page refresh.
  const [newProject, setNewProject] = useState("");
  // 2026-02 · Zoom lightbox — clicking the magnifier under the photo opens
  // the full-resolution image in a centred overlay. Kept as a plain img so it
  // works offline (no image lib dependency) and honours the R2 signed URL.
  const [zoomOpen, setZoomOpen] = useState(false);
  useEffect(() => {
    if (product) {
      const firstPurpose = (product.photos?.[0]?.purpose) || "";
      setForm({
        item_number: product.item_number || "",
        description: product.description || "",
        category: product.category || "",
        project: product.project || "",
        notes: product.notes || "",
        photo_type: firstPurpose,
        country_of_origin: product.country_of_origin || "",
        supplier: product.supplier || "",
      });
      setNewProject("");
    }
  }, [product]);

  // Prev/next inside the siblings list. Index is derived so we don't drift if
  // the parent mutates the list (e.g. after a filter change).
  const idx = useMemo(() => (siblings || []).findIndex((x) => x.id === product?.id), [siblings, product?.id]);
  const canPrev = idx > 0;
  const canNext = idx >= 0 && idx < (siblings?.length || 0) - 1;
  const goPrev = () => canPrev && onNavigate?.(siblings[idx - 1]);
  const goNext = () => canNext && onNavigate?.(siblings[idx + 1]);
  // Keyboard shortcuts (←/→) — a big usability win when bulk-editing hundreds
  // of survey photos. Ignore when the user is typing in an input/textarea.
  useEffect(() => {
    if (!product) return;
    const onKey = (e) => {
      const tag = (e.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || tag === "select") return;
      if (e.key === "ArrowLeft") { e.preventDefault(); goPrev(); }
      else if (e.key === "ArrowRight") { e.preventDefault(); goNext(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [product, idx, siblings]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    if (!product) return;
    setBusy(true);
    try {
      // If user picked "+ new project", persist it to the dict first so the
      // survey_products.project field stores the plain name (not "__new__").
      let projectName = form.project;
      if (form.project === "__new__") {
        const name = newProject.trim();
        if (!name) { toast.error(t("market.editProjectNewPlaceholder")); setBusy(false); return; }
        try {
          await api.post("/market/dict/projects", { name });
          await onDictReload?.();
        } catch (e) {
          // If it already exists the server returns {existing: true} — safe to
          // continue. Any other error surfaces to the user.
          const detail = e.response?.data?.detail;
          if (detail) { toast.error(formatApiErrorDetail(detail)); setBusy(false); return; }
        }
        projectName = name;
      }
      const { photo_type, ...rest } = form;
      const { data } = await api.patch(`/market/products/${product.id}`, { ...rest, project: projectName, photo_type });
      toast.success(t("market.saved"));
      setNewProject("");
      onSaved?.(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const remove = async () => {
    if (!product) return;
    if (!window.confirm(t("market.deleteConfirm"))) return;
    setBusy(true);
    try {
      await api.delete(`/market/products/${product.id}`);
      toast.success(t("market.deleted"));
      onSaved?.({ id: product.id, __deleted: true });
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <>
    <Dialog open={!!product} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-4xl" data-testid="market-edit-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between gap-3">
            <span>{t("market.editTitle")}</span>
            {/* Prev/Next paging — keeps users on the same dialog while they
                walk through the batch. Position counter helps them gauge
                progress ("12 / 300"). */}
            {siblings && siblings.length > 1 && idx >= 0 && (
              <div className="flex items-center gap-1.5 text-xs font-normal">
                <Button size="icon" variant="outline" onClick={goPrev} disabled={!canPrev || busy} data-testid="market-edit-prev" className="h-7 w-7">
                  <ChevronLeft size={14} />
                </Button>
                <span className="text-muted-foreground font-mono" data-testid="market-edit-position">{idx + 1} / {siblings.length}</span>
                <Button size="icon" variant="outline" onClick={goNext} disabled={!canNext || busy} data-testid="market-edit-next" className="h-7 w-7">
                  <ChevronRight size={14} />
                </Button>
              </div>
            )}
          </DialogTitle>
        </DialogHeader>
        {product && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Photo — 30% bigger than before (was 360 px → now 468 px, ~73% larger than the list thumb) */}
            <div className="space-y-2">
              <div className="w-[468px] max-w-full aspect-square bg-muted rounded-md overflow-hidden border border-border mx-auto">
                {product.photos?.[0] ? (
                  <img
                    src={photoFull(product.photos[0].url || product.photos[0].thumb_url)}
                    alt=""
                    className="w-full h-full object-contain"
                    data-testid="market-edit-photo"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-xs text-muted-foreground">{t("market.noImage")}</div>
                )}
              </div>
              <div className="text-[11px] text-muted-foreground font-mono flex items-center justify-between">
                <span>{product.store || "—"}</span>
                <span>{product.survey_date || ""}</span>
              </div>
              {/* 2026-02 · Magnifier — opens a lightbox so users can inspect
                  the full-resolution photo (fabric texture, hangtags, etc.)
                  without leaving the edit dialog. */}
              {product.photos?.[0] && (
                <div className="flex justify-center">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs gap-1 text-muted-foreground hover:text-foreground"
                    onClick={() => setZoomOpen(true)}
                    data-testid="market-edit-photo-zoom"
                  >
                    <ZoomIn size={13} /> {t("market.zoom")}
                  </Button>
                </div>
              )}
              {product.photos && product.photos.length > 1 && (
                <div className="text-[10px] text-muted-foreground text-center">{t("market.editPhotoTotal")} {product.photos.length} {t("market.photoCount")}</div>
              )}
            </div>

            <div className="space-y-3">
              <div>
                <label className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">{t("market.editProductId")}</label>
                <Input value={form.item_number} onChange={(e) => setForm((f) => ({ ...f, item_number: e.target.value }))} className="h-8 text-xs" data-testid="market-edit-item-number" />
              </div>
              <div>
                <label className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">{t("market.editDescription")}</label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                  rows={3}
                  className="w-full text-xs rounded-md border border-input bg-background px-3 py-1.5 resize-y min-h-[76px]"
                  data-testid="market-edit-description"
                />
              </div>
              <div>
                <label className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">{t("market.editCategory")}</label>
                <Select value={form.category || "__none__"} onValueChange={(v) => setForm((f) => ({ ...f, category: v === "__none__" ? "" : v }))}>
                  <SelectTrigger className="h-8 text-xs" data-testid="market-edit-category"><SelectValue placeholder={t("market.editCategoryPlaceholder")} /></SelectTrigger>
                  <SelectContent><SelectItem value="__none__">{t("market.editCategoryNone")}</SelectItem>{categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">{t("market.editProject")}</label>
                {/* 2026-02 · Reverted from free-text back to Select, per user
                    spec: pick from the /market/dict/projects dictionary with
                    an inline "+ new" option that adds to the dict on save. */}
                <Select
                  value={form.project === "" && form.project !== "__new__" ? "__none__" : form.project}
                  onValueChange={(v) => {
                    if (v === "__none__") setForm((f) => ({ ...f, project: "" }));
                    else setForm((f) => ({ ...f, project: v }));
                  }}
                >
                  <SelectTrigger className="h-8 text-xs" data-testid="market-edit-project">
                    <SelectValue placeholder={t("market.editProjectPlaceholder")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">{t("market.editCategoryNone")}</SelectItem>
                    {projects.map((p) => <SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>)}
                    <SelectItem value="__new__">+ {t("market.editProjectNew")}</SelectItem>
                  </SelectContent>
                </Select>
                {form.project === "__new__" && (
                  <Input
                    autoFocus
                    value={newProject}
                    onChange={(e) => setNewProject(e.target.value)}
                    placeholder={t("market.editProjectNewPlaceholder")}
                    className="mt-2 h-8 text-xs"
                    data-testid="market-edit-project-new-input"
                  />
                )}
              </div>
              <div>
                <label className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">{t("market.filterPurpose")}</label>
                <Select value={form.photo_type || "__none__"} onValueChange={(v) => setForm((f) => ({ ...f, photo_type: v === "__none__" ? "" : v }))}>
                  <SelectTrigger className="h-8 text-xs" data-testid="market-edit-photo-type"><SelectValue placeholder={t("market.filterPurpose")} /></SelectTrigger>
                  <SelectContent><SelectItem value="__none__">{t("market.editCategoryNone")}</SelectItem>{PHOTO_TYPES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {/* 2026-02 · Two new metadata fields requested by user: 原产地
                  (country of origin) + 供应商 (supplier). Free-form strings so
                  surveyors on the floor can type quickly without leaving the
                  dialog to manage yet another dictionary. */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">{t("market.editCountryOfOrigin")}</label>
                  <Input value={form.country_of_origin} onChange={(e) => setForm((f) => ({ ...f, country_of_origin: e.target.value }))} className="h-8 text-xs" data-testid="market-edit-country-of-origin" />
                </div>
                <div>
                  <label className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">{t("market.editSupplier")}</label>
                  <Input value={form.supplier} onChange={(e) => setForm((f) => ({ ...f, supplier: e.target.value }))} className="h-8 text-xs" data-testid="market-edit-supplier" />
                </div>
              </div>
              <div>
                <label className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">{t("market.editNotes")}</label>
                <textarea value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} rows={3} className="w-full text-xs rounded-md border border-input bg-background px-3 py-1.5" data-testid="market-edit-notes" />
              </div>
            </div>
          </div>
        )}
        <DialogFooter className="justify-between">
          <Button variant="destructive" size="sm" onClick={remove} disabled={busy} data-testid="market-edit-delete">
            <Trash2 size={13} className="mr-1" /> {t("market.delete")}
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} disabled={busy}>{t("market.cancel")}</Button>
            <Button onClick={save} disabled={busy} data-testid="market-edit-save">
              {busy ? <Loader2 size={14} className="animate-spin mr-1" /> : null} {t("market.save")}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    {/* Zoom lightbox — rendered as a sibling Dialog so it can layer above the
        edit dialog without breaking Radix's focus trap. */}
    <Dialog open={zoomOpen} onOpenChange={setZoomOpen}>
      <DialogContent className="max-w-[92vw] p-2 bg-black/95 border-none" data-testid="market-edit-photo-zoom-dialog">
        <DialogHeader className="sr-only"><DialogTitle>{t("market.zoom")}</DialogTitle></DialogHeader>
        {product?.photos?.[0] && (
          <img
            src={photoFull(product.photos[0].url || product.photos[0].thumb_url)}
            alt=""
            className="max-h-[85vh] w-auto mx-auto object-contain"
            data-testid="market-edit-photo-zoom-img"
          />
        )}
      </DialogContent>
    </Dialog>
    </>
  );
}
